// SEGMENT FİYATLANDIRMA — TEK KAYNAK
// Bayi = maliyet + %35 · Son kullanıcı = maliyet × 2 · Taban = maliyet + %35 (asla altına inilmez)
// Korumalı fiyatlar: manuel fiyat · müşteri/bayi grubu özel fiyatı · müşteri iskontosu
// Teklif Oluşturucu (QuoteBuilderPage) bu modülü kullanır — sipariş formu da aynı motordur (saveAsOrder)
// iki ekranın fiyatları GARANTİ birebir aynıdır ("kafa karışmaz" kuralı).

import { convertPrice } from "../appUtils";
import { STOR_MEKANIZMA_COST_USD } from "./quoteUtils";

export const SEGMENT_RATES = { bayi: 35, son_kullanici: 100 };
export const SEGMENT_RATE_MIN = 35; // taban: maliyet + %35

/** Müşterinin fiyat segmentine göre kâr oranı (%) — bayi 35, son kullanıcı 100 */
export function segmentRate(customer) {
  return (customer?.pricingSegment || "son_kullanici") === "bayi"
    ? SEGMENT_RATES.bayi
    : SEGMENT_RATES.son_kullanici;
}

/**
 * Kalem maliyeti (TL) — tedarikçi iskonto/matris kayıtlarından otomatik:
 * - Artbey/SkyZip: matris liste × alış çarpanı (Artbey −%52 → 0,48 | Bayedi T100/T125 −%57,5 → 0,425)
 * - Zip Aksesuar: asset.purchasePrice
 * - Stor/Zebra/Siluet/Motorlu Stor/Çiftli Sistem: kumaş + mekanizma (Impex USD/MT) + motor
 * Maliyet her zaman TL olarak döner. Dönen değer 0 ise maliyet bilinmiyor demektir.
 */
export function computeItemAutoCost({ slug, draftItem, assets = [], rates, currency, total }) {
  if (!rates || !draftItem) return 0;

  const isArtbeyZip = slug === "zip_art110_helio" || slug?.startsWith("skyzip");
  if (isArtbeyZip && total > 0) {
    const isBayediModel = ["T100", "T125"].includes(draftItem.zipCesidi || "");
    const buyRatio = isBayediModel ? 0.425 : 0.48; // Bayedi: %57,5 iskonto → alış %42,5 | Artbey: %52 iskonto → alış %48
    const fabricCostTL = Math.round(convertPrice(total * buyRatio, currency, "TL", rates, "cost"));
    let sensorCostTL = 0;
    if (draftItem.sensorLabel) {
      const sensorAsset = assets.find(a => a.kind === "sensor" && a.label === draftItem.sensorLabel);
      if (sensorAsset?.purchasePrice > 0) {
        const sAmt = sensorAsset.purchasePrice * (draftItem.qty || 1);
        sensorCostTL = Math.round(convertPrice(sAmt, sensorAsset.purchaseCurrency || "EUR", "TL", rates, "cost"));
      }
    }
    return fabricCostTL + sensorCostTL;
  }

  if (slug === "zip_aksesuar") {
    const accAsset = assets.find(a =>
      (a.kind === "sensor" || a.kind === "control") && a.label === draftItem.product
    );
    const purchaseAmt = (accAsset?.purchasePrice || Number(draftItem.manualUnitPrice) || 0) * (draftItem.qty || 1);
    return Math.round(convertPrice(purchaseAmt, accAsset?.purchaseCurrency || "EUR", "TL", rates, "cost"));
  }

  const STOR_AUTO_COST_SLUGS = ["stor", "zebra", "silhouette", "motorlu_stor", "ciftli_sistem"];
  if (STOR_AUTO_COST_SLUGS.includes(slug)) {
    const wMT = (Number(draftItem.widthCm) || 0) / 100;
    const hMT = (Number(draftItem.heightCm) || 0) / 100;
    const pQty = draftItem.qty || 1;
    const m2 = wMT * hMT * pQty;
    // 1. Kumaş alış maliyeti (kumaşın kendi para biriminden TL'ye çevrilir — alış kuru ile)
    const fabricCostTL = convertPrice(
      Number(draftItem.fabricPurchase) || 0,
      draftItem.fabricPurchaseCurrency || "TL",
      "TL", rates, "cost"
    ) * m2;
    // 2. Mekanizma alış maliyeti (Impex USD/MT → TL)
    const mechCostUSD = (STOR_MEKANIZMA_COST_USD[draftItem.mekanizma] || 0) * wMT * pQty;
    const mechCostTL = mechCostUSD * (rates.usdToTl || 34);
    // 3. Motor alış maliyeti (asset.purchasePrice USD/EUR → TL)
    const motorAsset = draftItem.motorLabel
      ? assets.find(a => a.kind === "motor" && a.label === draftItem.motorLabel)
      : null;
    const motorPurchaseAmt = (motorAsset?.purchasePrice || 0) * pQty;
    const motorCostTL = motorPurchaseAmt > 0
      ? convertPrice(motorPurchaseAmt, motorAsset.purchaseCurrency || "USD", "TL", rates, "cost")
      : 0;
    return Math.round(fabricCostTL + mechCostTL + motorCostTL);
  }

  return 0;
}

/**
 * Segment fiyatlandırması: maliyeti bilinen ve KORUNMASIZ kalemleri maliyet × (1+oran) ile fiyatla.
 * Korunanlar: manuel fiyat · müşteri/bayi grubu kuralı · müşteri iskontosu.
 * GLOBAL Kâr Marjı (liste bazlı) ile çışırsa segment fiyatı kazanır — ÇAKIŞMA ÇÖZÜMÜ.
 * Yeni fiyat `marginPriced` ile işaretlenir (MARJ rozeti); kural etiketi temizlenir.
 */
export function applySegmentPricing(items, itemCosts, rates, rate) {
  return items.map((it, i) => {
    // 1) Manuel fiyat — MANUEL FİYAT KUTSALDIR
    if (it.isManualPrice || Number(it.manualUnitPrice) > 0) return it;
    // 2) Müşteri veya bayi grubuna özel fiyat kuralı — asla ezilmez
    if (it.dealerRuleLabel && it.ruleType !== "global") return it;
    // 3) Müşteri iskontosu uygulanmış (müşteriye özel fiyat)
    if (Number(it.discountPercent) > 0) return it;
    const costTL = Number(itemCosts[i]);
    if (!costTL) return it;
    const nativeCur = it.currency || "TL";
    const costNative = convertPrice(costTL, "TL", nativeCur, rates, "cost");
    const newTotal = Math.round(costNative * (1 + rate / 100) * 100) / 100;
    return {
      ...it,
      lineTotal: newTotal,
      listLineTotal: newTotal,
      unitPrice: Math.round(newTotal / (it.qty || 1) * 100) / 100,
      discountPercent: 0,
      isManualPrice: false,
      dealerRuleLabel: null, ruleType: null,
      marginPriced: rate,
    };
  });
}

/** Taban kontrolü: fiyat (kalem para birimi) maliyet+%35'in altında mı? */
export function isBelowSegmentFloor(lineTotal, costTL, itemCurrency, rates) {
  if (!Number(costTL) || !Number(lineTotal)) return false;
  const floorNative = convertPrice(Number(costTL) * (1 + SEGMENT_RATE_MIN / 100), "TL", itemCurrency || "TL", rates, "cost");
  return Number(lineTotal) < floorNative;
}
